import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { SchemaItem, SchemaSection } from '@rossum/api-client/schemas';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import { EmptyState } from '@rossum/components/empty-state/EmptyState';
import { Tile } from '@rossum/components/tile';
import { IconTrash } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  Stack,
  TextField,
  Typography,
} from '@rossum/ui/material';
import { FormulaEditor } from '../../../../../formulas/FormulaEditor/FormulaEditor';
import { Variable, VariablesMap } from './types';

type VariablesProps = {
  open: boolean;
  onClose: () => void;
  variables: VariablesMap;
  onChange: (variables: VariablesMap) => void;
  queriesValue: string;
  schemaFields: ((SchemaItem | SchemaSection) & {
    isInsideMultivalue: boolean;
  })[];
  variableToEdit: string | null;
};

type VariableItemProps = {
  variable: Variable;
  index: number;
  schemaFields: ((SchemaItem | SchemaSection) & {
    isInsideMultivalue: boolean;
  })[];
  defaultExpanded: boolean;
  onRemove: (index: number) => void;
  onChange: (index: number, variable: Omit<Variable, 'id'>) => void;
};

const VariableItem = ({
  variable,
  index,
  schemaFields,
  defaultExpanded,
  onRemove,
  onChange,
}: VariableItemProps) => {
  const [expanded, setExpanded] = useState(
    defaultExpanded || variable.name === ''
  );

  return (
    <Tile
      title={variable.name || 'New Variable'}
      description={variable.value || 'No formula defined'}
      quickActions={[
        {
          key: 'delete',
          iconProps: {
            Icon: IconTrash,
            state: false,
            disabled: false,
          },
          onClick: () => onRemove(index),
        },
      ]}
      expandable={{
        expanded,
        onToggleExpanded: () => setExpanded(prev => !prev),
        content: (
          <Stack spacing={2} p={3}>
            <TextField
              label="Variable Name"
              fullWidth
              size="small"
              autoFocus={!variable.name}
              value={variable.name}
              onChange={e =>
                onChange(index, { ...variable, name: e.target.value })
              }
            />
            <FormulaEditor
              value={variable.value}
              onChange={value => onChange(index, { ...variable, value })}
              onBlur={() => {}}
              fields={schemaFields}
              config={{
                maxVisibleLines: 1,
                minVisibleLines: 1,
                showGutters: false,
                paddingTop: 2,
                paddingBottom: 2,
              }}
              onLoad={editor => {
                // Disable Enter key to prevent newlines
                editor.commands.bindKey('Return', () => null);
                editor.commands.bindKey('Shift-Return', () => null);

                // Remove newlines from pasted content
                editor.on('paste', (e: { text?: string }) => {
                  const text = e.text?.replace(/\n|\r/g, ' ') || '';
                  if (text !== e.text) {
                    e.text = text;
                  }
                });
              }}
            />
          </Stack>
        ),
      }}
      dragHandle={null}
    />
  );
};

export const Variables = ({
  open,
  onClose,
  variables: variablesProp,
  onChange,
  queriesValue,
  schemaFields,
  variableToEdit,
}: VariablesProps) => {
  const intl = useIntl();

  const [variables, setVariables] = useState<Variable[]>(
    variablesProp
      ? Object.entries(variablesProp).map(([key, variable], index) => ({
          id: index,
          name: key,
          value: variable.__formula,
        }))
      : []
  );

  const handleUpdateVariable = useCallback(
    (index: number, updatedVariable: Omit<Variable, 'id'>) => {
      setVariables(currentVariables =>
        currentVariables.map((v, i) =>
          i === index ? { ...v, ...updatedVariable } : v
        )
      );
    },
    []
  );

  const handleAddVariable = useCallback(() => {
    setVariables(currentVariables => [
      ...currentVariables,
      { id: Date.now(), name: '', value: '' },
    ]);
  }, []);

  const handleRemoveVariable = useCallback((index: number) => {
    setVariables(currentVariables =>
      currentVariables.filter((_, i) => i !== index)
    );
  }, []);

  const handleCancel = () => {
    onClose();
  };

  const handleSave = () => {
    const newVariables: VariablesMap = {};
    variables.forEach(variable => {
      if (variable.name.trim()) {
        newVariables[variable.name.trim()] = {
          __formula: variable.value || '',
        };
      }
    });
    onChange(newVariables);
    onClose();
  };

  const unusedVariables = useMemo(() => {
    return variables.filter(variable => {
      const variableReference = `$$${variable.name}`;
      return variable.name && !queriesValue.includes(variableReference);
    });
  }, [variables, queriesValue]);

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle title="Variables" onClose={handleCancel} />
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 2 }}>
          {unusedVariables.length > 0 && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight="medium">
                {intl.formatMessage({
                  id: 'features.queueSettings.customLookup.unusedVariables',
                })}
              </Typography>
              <Typography variant="body2">
                {unusedVariables.map(v => v.name).join(', ')}
              </Typography>
            </Alert>
          )}

          {variables.length === 0 ? (
            <EmptyState
              title={intl.formatMessage({
                id: 'features.queueSettings.customLookup.noVariables',
              })}
              description={intl.formatMessage({
                id: 'features.queueSettings.customLookup.noVariablesDescription',
              })}
            />
          ) : (
            <List
              dense
              sx={{
                py: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              {variables.map((variable, index) => (
                <VariableItem
                  key={variable.id}
                  index={index}
                  variable={variable}
                  schemaFields={schemaFields}
                  defaultExpanded={variableToEdit === variable.name}
                  onRemove={handleRemoveVariable}
                  onChange={handleUpdateVariable}
                />
              ))}
            </List>
          )}

          <Stack direction="column" alignItems="flex-end">
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleAddVariable}
            >
              {intl.formatMessage({
                id: 'features.queueSettings.customLookup.addVariable',
              })}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button variant="outlined" color="secondary" onClick={handleCancel}>
          {intl.formatMessage({ id: 'components.modal.cancel' })}
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {intl.formatMessage({ id: 'components.editor.tooltips.save' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
